import { BarChart } from '@/components/ui/bar-chart';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePayrollAnalyticsBoard } from '@/hooks/usePayrollAnalyticsBoard';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ServiceEmployeeSectionProps } from '../types';

// Chart colors from shadcn theme
const CHART_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

const formatTooltipValue = (value: number) => `$${value.toLocaleString()}`;

const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div
      style={{
        backgroundColor: 'white',
        padding: '0.75rem 1rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        position: 'relative',
        zIndex: 99999,
      }}
    >
      <p className="text-sm font-medium">{payload[0].name}</p>
      <p className="text-sm font-semibold">
        {typeof payload[0].value === 'number' ? formatTooltipValue(payload[0].value) : '0'}
      </p>
    </div>
  );
};

interface LegendPayloadItem {
  value: string;
  color: string;
  payload: {
    value: number;
  };
}

const CustomLegend = ({ payload }: { payload?: LegendPayloadItem[] }) => {
  if (!payload || payload.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 w-full text-sm px-2">
      {payload.map((entry, index) => (
        <div key={`legend-${index}`} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="font-medium whitespace-nowrap">{entry.value}</span>
          <span className="font-semibold ml-auto">
            ${entry.payload?.value?.toLocaleString() ?? 0}
          </span>
        </div>
      ))}
    </div>
  );
};

const ServiceEmployeeSection = ({
  selectedYear,
  selectedMonth,
  onYearChange,
  onMonthChange,
}: ServiceEmployeeSectionProps) => {
  // Fetch and process data using custom hook
  const {
    serviceIncomeData,
    employeeData,
    currentTotal,
    percentageChange,
    isLoading,
    isError,
  } = usePayrollAnalyticsBoard(selectedYear, selectedMonth);

  // Preserve scroll position during re-renders
  useEffect(() => {
    const scrollPosition = window.scrollY;
    return () => window.scrollTo(0, scrollPosition);
  }, [selectedYear, selectedMonth]);

  // Get month name
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = monthNames[selectedMonth];

  // Filter out services with no income and format data for bar chart
  const activeServices = serviceIncomeData.filter(service => service.value > 0);
  const barChartData = activeServices.map(item => ({
    service: item.name,
    income: item.value,
  }));

  // Loading state
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Unable to load service and employee analytics. Please refresh or try again.
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      <Card className="border-none">
        <CardContent className="p-4">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <div className="flex flex-col">
              <CardTitle>Service & Employee Income</CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl font-bold">${currentTotal.toLocaleString()}</span>
                <div
                  className={`flex items-center gap-1 text-sm ${
                    percentageChange >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {percentageChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                  {Math.abs(percentageChange).toFixed(1)}%
                </div>
                <span className="text-xs text-gray-500">vs previous month</span>
              </div>
            </div>
            <div className="flex gap-2 mt-4 sm:mt-0">
              <Select
                value={selectedYear.toString()}
                onValueChange={value => onYearChange?.(parseInt(value))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={selectedMonth.toString()}
                onValueChange={value => onMonthChange?.(parseInt(value))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => ({
                    value: i,
                    label: new Date(2000, i).toLocaleString('default', { month: 'long' }),
                  })).map(month => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bar Chart - Services Income */}
            <div className="h-[200px] sm:h-[270px]">
              <CardTitle className="text-sm mb-12">Income by Service</CardTitle>
              <BarChart
                data={barChartData}
                index="service"
                categories={['income']}
                colors={[CHART_COLORS[1]]}
                valueFormatter={(value: number) => `$${value.toLocaleString()}`}
              />
            </div>

            {/* Pie Chart - Employee Income */}
            <div className="pt-12 pb-6 sm:pt-0">
              <CardTitle className="text-sm mb-4">Employee Income Distribution</CardTitle>
              <div className="flex flex-col items-center">
                <div className="relative w-full h-[350px]" style={{ zIndex: 40 }}>
                  <div className="h-[250px] w-full relative" style={{ zIndex: 50 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                        <Pie
                          data={employeeData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          {employeeData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={CHART_COLORS[index % CHART_COLORS.length]}
                              strokeWidth={0}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 99999 }} />
                        <Legend
                          content={<CustomLegend />}
                          verticalAlign="bottom"
                          height={50}
                          wrapperStyle={{
                            bottom: 0,
                            width: '100%',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center flex-col">
                      <span className="text-2xl font-bold">${currentTotal.toLocaleString()}</span>
                      <span className="text-sm text-gray-500">
                        {monthName} {selectedYear}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceEmployeeSection;
